/**
 * A centred dialog over a dimmed page.
 *
 * Rendered into document.body rather than in place: the auth card has its own
 * stacking context and overflow, and a dialog rendered inside one is clipped
 * by it and sits under whatever has a higher z-index than its parent.
 */
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** The action that dismisses it. Defaults to "Got it". */
  confirmLabel?: string;
}

export function Modal({ open, onClose, title, children, confirmLabel = "Got it" }: ModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    // Focus the one action, so Enter or Space dismisses without reaching for
    // the mouse, and a screen reader lands somewhere useful.
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          // Clicking the backdrop dismisses, but only the backdrop itself:
          // without the target check, a click that starts inside the dialog
          // and drifts out closes it mid-sentence.
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.99 }}
            transition={{ duration: 0.18, ease: [0.3, 0, 0.2, 1] }}
          >
            <h2 className="modal-title" id="modal-title">{title}</h2>
            <div className="modal-body">{children}</div>
            <button ref={confirmRef} className="btn primary modal-ok" onClick={onClose}>
              {confirmLabel}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
